// src/pages/SubdomainSetupPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSubdomain } from '../contexts/SubdomainContext';
import { isValidSubdomain, checkSubdomainAvailability } from '../services/subdomainService';
import { registerSubdomainAuto } from '../services/autoSubdomainService';
import { generateAndSaveSubdomainHTML } from '../services/htmlGeneratorService';
import LoadingSpinner from '../components/LoadingSpinner';
import '../styles/pages/SubdomainSetupPage.scss';

const SubdomainSetupPage = () => {
    const { currentUser, accountId } = useAuth();
    const { hasSubdomain, refreshSubdomain } = useSubdomain();
    const navigate = useNavigate();
    const { accountId: paramAccountId } = useParams();
    const [subdomain, setSubdomain] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [availability, setAvailability] = useState(null);
    const [isChecking, setIsChecking] = useState(false);
    const [step, setStep] = useState('welcome'); // 'welcome', 'input', 'processing', 'complete'

    useEffect(() => {
        // 認証チェック
        if (!currentUser) {
            navigate('/login');
            return;
        }

        // accountIdの整合性チェック
        if (paramAccountId && accountId !== paramAccountId) {
            navigate(`/subdomain-setup/${accountId}`);
            return;
        }

        // 既にサブドメインが登録されている場合はダッシュボードへ
        if (hasSubdomain) {
            // 3秒後にダッシュボードにリダイレクト（ユーザーに情報を表示するため）
            setTimeout(() => {
                navigate(`/dashboard/${accountId}`);
            }, 3000);
            return;
        }
    }, [currentUser, accountId, paramAccountId, hasSubdomain, navigate]);

    const handleSubdomainChange = (e) => {
        const value = e.target.value.toLowerCase();
        setSubdomain(value);
        setError('');
        setAvailability(null);

        // リアルタイムで形式チェック
        if (value && !isValidSubdomain(value)) {
            setError('サブドメインは英数字とハイフンのみ使用可能です（3-63文字）');
        }
    };

    const checkAvailability = async () => {
        if (!subdomain || !isValidSubdomain(subdomain)) {
            return;
        }

        setIsChecking(true);
        try {
            const isAvailable = await checkSubdomainAvailability(subdomain);
            setAvailability(isAvailable);
        } catch (error) {
            setError('利用可能性のチェックに失敗しました');
        } finally {
            setIsChecking(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!subdomain || !isValidSubdomain(subdomain)) {
            setError('有効なサブドメインを入力してください');
            return;
        }

        setIsLoading(true);
        setError('');
        setStep('processing');

        try {
            console.log('🚀 サブドメイン登録を開始:', subdomain);

            // 自動承認方式でサブドメインを登録
            const result = await registerSubdomainAuto(subdomain, currentUser.uid, currentUser.email);

            if (result.success) {
                // サブドメイン登録後、HTMLファイルを生成
                try {
                    await generateAndSaveSubdomainHTML(subdomain);
                    console.log('✅ サブドメインHTMLファイルが生成されました');
                } catch (htmlError) {
                    console.error('⚠️ サブドメインHTMLファイル生成エラー:', htmlError);
                    // HTMLファイル生成に失敗してもサブドメイン登録は成功とする
                }

                // サブドメイン情報を更新
                await refreshSubdomain();

                setStep('complete');

                // 3秒後にダッシュボードにリダイレクト
                setTimeout(() => {
                    navigate(`/dashboard/${accountId}`, {
                        state: {
                            message: `🎉 サブドメイン「${subdomain}.suisui.life」の登録が完了しました！`,
                            success: true
                        }
                    });
                }, 3000);
            } else {
                setError(result.error || 'サブドメインの登録に失敗しました');
                setStep('input');
            }
        } catch (error) {
            console.error('❌ サブドメイン登録エラー:', error);
            setError(error.message || 'サブドメインの登録に失敗しました');
            setStep('input');
        } finally {
            setIsLoading(false);
        }
    };

    const getAvailabilityMessage = () => {
        if (availability === true) {
            return <span className="availability-message available">✅ 利用可能です</span>;
        } else if (availability === false) {
            return <span className="availability-message unavailable">❌ 既に使用されています</span>;
        }
        return null;
    };

    // 既にサブドメインが設定されている場合の表示
    if (hasSubdomain) {
        return (
            <div className="subdomain-setup-container">
                <div className="setup-card already-setup-card">
                    <div className="welcome-icon">✅</div>
                    <h1>サブドメインは既に設定済みです</h1>
                    <p className="already-setup-message">
                        このアカウントには既にサブドメインが設定されています。<br />
                        サブドメインは1アカウントにつき1つのみ設定可能です。
                    </p>
                    <div className="subdomain-display">
                        <strong>設定済みのサブドメイン:</strong>
                        <a
                            href={`https://${subdomain.subdomain}.suisui.life`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="subdomain-link"
                        >
                            {subdomain.subdomain}.suisui.life
                        </a>
                    </div>
                    <p className="redirect-message">
                        3秒後にダッシュボードにリダイレクトします...
                    </p>
                    <button
                        onClick={() => navigate(`/dashboard/${accountId}`)}
                        className="btn-primary"
                    >
                        今すぐダッシュボードへ
                    </button>
                </div>
            </div>
        );
    }

    if (step === 'welcome') {
        return (
            <div className="subdomain-setup-container">
                <div className="setup-card welcome-card">
                    <div className="welcome-icon">🌐</div>
                    <h1>Suisui CMS へようこそ！</h1>
                    <p className="welcome-message">
                        Suisui CMSをご利用いただくには、あなた専用のサブドメインが必要です。<br />
                        サブドメインを設定することで、あなただけのウェブサイトを作成・公開できます。
                    </p>

                    <div className="features">
                        <div className="feature">
                            <div className="feature-icon">🎨</div>
                            <h3>カスタムサイト</h3>
                            <p>あなただけのドメインでウェブサイトを公開</p>
                        </div>
                        <div className="feature">
                            <div className="feature-icon">⚡</div>
                            <h3>高速配信</h3>
                            <p>Cloudflareを使った世界規模の高速配信</p>
                        </div>
                        <div className="feature">
                            <div className="feature-icon">🔒</div>
                            <p>SSL証明書付きで安全なサイト運営</p>
                        </div>
                    </div>

                    <button
                        onClick={() => setStep('input')}
                        className="get-started-button"
                    >
                        サブドメインを設定する
                    </button>
                </div>
            </div>
        );
    }

    if (step === 'processing') {
        return (
            <div className="subdomain-setup-container">
                <div className="setup-card processing-card">
                    <LoadingSpinner />
                    <h2>サブドメインを設定中...</h2>
                    <p>DNS設定とサイトの初期化を行っています。しばらくお待ちください。</p>
                    <div className="processing-steps">
                        <div className="step active">✅ サブドメインを予約</div>
                        <div className="step active">⏳ DNS設定を構成</div>
                        <div className="step">⏳ サイトを初期化</div>
                    </div>
                </div>
            </div>
        );
    }

    if (step === 'complete') {
        return (
            <div className="subdomain-setup-container">
                <div className="setup-card complete-card">
                    <div className="success-icon">🎉</div>
                    <h2>設定完了！</h2>
                    <p>
                        サブドメイン「<strong>{subdomain}.suisui.life</strong>」の設定が完了しました。
                    </p>
                    <p>
                        まもなくダッシュボードにリダイレクトします...
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="subdomain-setup-container">
            <div className="setup-card">
                <button
                    onClick={() => setStep('welcome')}
                    className="back-button"
                    type="button"
                >
                    ← 戻る
                </button>

                <h1>サブドメインを設定</h1>
                <p className="subtitle">
                    あなた専用のサブドメインを決定してください。<br />
                    例: <strong>yourname</strong>.suisui.life
                </p>

                <form onSubmit={handleSubmit} className="subdomain-form">
                    <div className="input-group">
                        <label htmlFor="subdomain">サブドメイン</label>
                        <div className="subdomain-input-wrapper">
                            <input
                                type="text"
                                id="subdomain"
                                value={subdomain}
                                onChange={handleSubdomainChange}
                                placeholder="yourname"
                                className={error ? 'error' : ''}
                                disabled={isLoading}
                                autoFocus
                            />
                            <span className="domain-suffix">.suisui.life</span>
                        </div>
                        {error && <div className="error-message">{error}</div>}
                        {getAvailabilityMessage()}
                    </div>

                    <div className="form-actions">
                        <button
                            type="button"
                            onClick={checkAvailability}
                            disabled={!subdomain || !isValidSubdomain(subdomain) || isChecking}
                            className="check-button"
                        >
                            {isChecking ? '確認中...' : '利用可能かチェック'}
                        </button>

                        <button
                            type="submit"
                            disabled={!subdomain || !isValidSubdomain(subdomain) || availability !== true || isLoading}
                            className="submit-button"
                        >
                            {isLoading ? '設定中...' : 'サブドメインを設定'}
                        </button>
                    </div>
                </form>

                <div className="help-text">
                    <h3>サブドメインについて</h3>
                    <ul>
                        <li>英数字とハイフン（-）のみ使用可能</li>
                        <li>3〜63文字で入力してください</li>
                        <li>後から変更することはできません</li>
                        <li>すべて小文字で入力されます</li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default SubdomainSetupPage;
